import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useTranslation } from 'react-i18next';
import { formatDateTimeInline } from '../../../utils';
import { detailCardSx } from './detailSection.component';

export interface DetailMetadataItem {
  icon?: React.ReactNode;
  label: React.ReactNode;
  value: React.ReactNode;
}

interface DetailMetadataProps {
  createdAt?: string | null;
  updatedAt?: string | null;
  /** Who created the record - usually a {@link UserDisplay} with `size="small"`. */
  createdBy?: React.ReactNode;
  /** Other audit facts, shown after the standard ones. */
  items?: DetailMetadataItem[];
}

const MetadataItem = ({ icon, label, value }: DetailMetadataItem) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
    {icon && <Box sx={{ display: 'flex', color: 'text.disabled', '& svg': { fontSize: 16 } }}>{icon}</Box>}
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
    <Typography variant="body2" color="text.secondary" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
      {value}
    </Typography>
  </Box>
);

/**
 * The audit footer closing every detail view: when the record was created and
 * last changed, and by whom. Renders nothing when there's nothing to show.
 */
export const DetailMetadata = ({ createdAt, updatedAt, createdBy, items = [] }: DetailMetadataProps) => {
  const { t } = useTranslation();

  const allItems: DetailMetadataItem[] = [
    ...(createdAt
      ? [{ icon: <AddCircleOutlineIcon />, label: t('Created At'), value: formatDateTimeInline(createdAt) }]
      : []),
    ...(createdBy ? [{ icon: <PersonOutlineIcon />, label: t('Created By'), value: createdBy }] : []),
    ...(updatedAt
      ? [{ icon: <EditOutlinedIcon />, label: t('Updated At'), value: formatDateTimeInline(updatedAt) }]
      : []),
    ...items.filter((item) => item.value !== null && item.value !== undefined && item.value !== ''),
  ];

  if (allItems.length === 0) return null;

  return (
    <Box
      component="footer"
      sx={{
        ...detailCardSx,
        bgcolor: 'action.hover',
        px: 2,
        py: 1.25,
        display: 'flex',
        flexWrap: 'wrap',
        columnGap: 3,
        rowGap: 1,
      }}
    >
      {allItems.map((item, index) => (
        <MetadataItem key={index} {...item} />
      ))}
    </Box>
  );
};
